import { defineComponent, ref, getCurrentInstance, computed, onBeforeMount, watch } from 'vue-demi';
import _ from 'lodash';
import { useMounted } from '@vueuse/core';
import { s as script$4 } from './ServiceNavigationDropdown-6e353052.js';
export { U as UserRoles } from './ServiceNavigationDropdown-6e353052.js';
import { s as script$2 } from './ServiceNavigationLogo-801a3734.js';
import { s as script$3 } from './ServiceNavigationContent-077741fa.js';
import { s as script$5 } from './ServiceNavigationFooter-12996c14.js';
import { s as script$1 } from './ServiceNavigationUtilitiesMenu-42350818.js';
import { s as script$6 } from './ServiceNavigationNotifications-62d2e393.js';
import { SERVICES } from './constants/index.js';
import { OrConfirmV3, useToastV3, useResponsive } from '@onereach/ui-components';
import { u as useServiceDiscoveryUiServices, a as useAccountSettings, b as useIdwService } from './useAccountSettings-bcd0cc43.js';
import { u as useUserServiceNavigationFavoritesServicesSettings } from './useUserServiceNavigationFavoritesServicesSettings-5c354879.js';
import '@or-sdk/notifications';
import 'lodash/orderBy';
import 'lodash/uniqBy';
import { u as useI18n } from './useI18n-25380ee3.js';
import { resolveComponent, openBlock, createElementBlock, createElementVNode, createVNode, withCtx, renderSlot, createBlock, createCommentVNode, createTextVNode, toDisplayString } from 'vue';
export { s as ServiceNavigationBreadcrumbs } from './ServiceNavigationBreadcrumbs-029b507d.js';
export { s as ServiceNavigationMenu } from './ServiceNavigationMenu-4efbe94f.js';
import 'tailwind-merge';
import '@or-sdk/chat';
import './ServiceNavigationButton-4dfba521.js';
import './EmojiPicker-4cfbc9d9.js';
import '@onereach/emoji-picker-element';
import './useNotifications-d3f6e9f2.js';
import '@or-sdk/discovery';
import '@or-sdk/settings';
import '@or-sdk/idw';
import '@or-sdk/account-settings';

function prepareLocaleMessage(serviceKey, translations) {
  return _.mapValues(translations, t => ({
    services: {
      [_.kebabCase(serviceKey)]: t
    }
  }));
}
function prepareLocaleMessages(services) {
  return _.mapValues(_.groupBy(_.flatMap(services, s => _.map(_.entries(s.translations), t => ({
    key: s.serviceKey,
    locale: t[0],
    translation: t[1]
  }))), msg => msg.locale), msgs => ({
    'services': _.fromPairs(_.map(msgs, msg => [msg.key, msg.translation]))
  }));
}
function getServiceLabel(service) {
  return `services.${_.isString(service) ? service : service.serviceKey}.label`;
}
function extractDropdownItems(services, activeLabel, hiddenOnAccount) {
  var _a, _b;
  let lastOrder = (_a = services === null || services === void 0 ? void 0 : services.reduce((prev, curr) => curr.order ? Math.max(prev, curr.order) : prev, 0)) !== null && _a !== void 0 ? _a : 0;
  return (_b = services === null || services === void 0 ? void 0 : services.map(item => {
    var _a, _b, _c, _d, _e;
    return {
      label: getServiceLabel(item),
      active: activeLabel === getServiceLabel(item),
      url: (_a = item.url) !== null && _a !== void 0 ? _a : '/',
      id: item.serviceKey,
      order: (_b = item.order) !== null && _b !== void 0 ? _b : ++lastOrder,
      logo: (_c = item.logo) !== null && _c !== void 0 ? _c : '',
      hiddenFromAccount: (_d = hiddenOnAccount === null || hiddenOnAccount === void 0 ? void 0 : hiddenOnAccount.includes(item.serviceKey)) !== null && _d !== void 0 ? _d : false,
      hiddenFromEnvironment: (_e = item.envHidden) !== null && _e !== void 0 ? _e : false
    };
  })) !== null && _b !== void 0 ? _b : [];
}
function extractServiceLocaleMessages(service, translations) {
  var _a;
  const serviceKey = (_a = SERVICES[service]) !== null && _a !== void 0 ? _a : service;
  return prepareLocaleMessage(serviceKey, translations);
}
function getUtilitiesMenuItemsConfig(config, token = '') {
  return {
    ...config.UTILS_MENU_LIST,
    token
  };
}
function getHomePageUrl(config) {
  return config.REPORTING_URL;
}

var script = defineComponent({
  name: 'ServiceNavigationHeader',
  components: {
    ServiceNavigationUtilitiesMenu: script$1,
    ServiceNavigationLogo: script$2,
    ServiceNavigationContent: script$3,
    ServiceNavigationDropdown: script$4,
    ServiceNavigationFooter: script$5,
    ServiceNavigationNotifications: script$6,
    OrConfirmV3
  },
  props: {
    showUtilsMenu: {
      type: Boolean,
      default: true
    },
    config: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      required: false,
      default: undefined
    },
    token: {
      type: String,
      required: false,
      default: null
    },
    showServices: {
      type: Boolean,
      default: true
    },
    serviceTranslations: {
      type: Object,
      default: null
    },
    breadcrumbItems: {
      type: Array,
      default: () => []
    },
    menuItems: {
      type: Array,
      default: () => []
    },
    showNotifications: {
      type: Boolean,
      default: true
    },
    accountId: {
      type: String,
      default: undefined
    },
    userRole: {
      type: String,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    const deleteConfirm = ref();
    const serviceToDelete = ref();
    const isRemovingService = ref(false);
    const isMounted = useMounted();
    const {
      initializeI18n,
      mergeLocaleMessages,
      t
    } = useI18n();
    const {
      success
    } = useToastV3();
    const {
      isDesktop
    } = useResponsive();
    const instance = getCurrentInstance();
    const {
      getUserFavoritesServices,
      toggleUserFavoritesServices
    } = useUserServiceNavigationFavoritesServicesSettings(() => props.token, {
      sdkUrl: props.config.SDK_API_URL
    });
    const {
      uiServices,
      deleteService,
      hideService: hideFromEnvironment,
      revealService: revealOnEnvironment
    } = useServiceDiscoveryUiServices(() => props.token, {
      discoveryUrl: props.config.SERVICE_DISCOVERY_API_URL
    }, props.config.FEATURE);
    const accountSettingsToken = computed(() => props.showServices ? props.token : '');
    const {
      hiddenServices,
      hideService,
      showService
    } = useAccountSettings(accountSettingsToken, {
      sdkUrl: props.config.SDK_API_URL
    });
    const {
      isIdwAccount,
      idwInfo
    } = useIdwService(() => props.token, {
      discoveryUrl: props.config.SERVICE_DISCOVERY_API_URL,
      emUrl: props.config.EM_URL,
      providerAccountId: props.config.PROVIDERS_ACCOUNT_ID
    });
    const service = computed(() => props.config.service);
    const utilitiesMenuItemsConfig = computed(() => getUtilitiesMenuItemsConfig(props.config, props.token));
    const logoUrl = computed(() => props.config.LOGO_URL || undefined);
    const targetUrl = computed(() => getHomePageUrl(props.config));
    const projectLabel = computed(() => getServiceLabel(_.kebabCase(props.config.service)));
    const dropdownItems = computed(() => extractDropdownItems(uiServices.value, projectLabel.value, hiddenServices.value));
    async function hideServiceFromAccount(serviceId) {
      await hideService(serviceId);
      success({
        messageText: t('hideAccountToastMessage', {
          service: t(`services.${serviceId}.label`)
        })
      });
    }
    function revealServiceOnAccount(serviceId) {
      showService(serviceId);
    }
    async function hideServiceFromEnvironment(serviceId) {
      await hideFromEnvironment(serviceId);
      success({
        messageText: t('hideEnvironmentToastMessage', {
          service: t(`services.${serviceId}.label`)
        })
      });
    }
    function revealServiceOnEnvironment(serviceId) {
      revealOnEnvironment(serviceId);
    }
    function onDelete(serviceId) {
      var _a;
      serviceToDelete.value = serviceId;
      (_a = deleteConfirm.value) === null || _a === void 0 ? void 0 : _a.open();
    }
    async function onConfirmDelete() {
      var _a;
      if (serviceToDelete.value) {
        isRemovingService.value = true;
        await deleteService(serviceToDelete.value);
        isRemovingService.value = false;
        (_a = deleteConfirm.value) === null || _a === void 0 ? void 0 : _a.close();
        success({
          messageText: t('deleteToastMessage', {
            service: t(`services.${serviceToDelete.value}.label`)
          })
        });
      }
    }
    onBeforeMount(() => {
      initializeI18n(instance);
      const localeMessages = extractServiceLocaleMessages(props.config.service, props.serviceTranslations);
      mergeLocaleMessages(localeMessages);
    });
    watch([uiServices, isMounted], ([value, mounted]) => {
      if (!(value === null || value === void 0 ? void 0 : value.length) || !mounted) return;
      const localeMessages = prepareLocaleMessages(value);
      mergeLocaleMessages(localeMessages);
    });
    return {
      service,
      deleteConfirm,
      logoUrl,
      targetUrl,
      projectLabel,
      uiServices,
      serviceToDelete,
      t,
      dropdownItems,
      utilitiesMenuItemsConfig,
      deleteService,
      onConfirmDelete,
      onDelete,
      hiddenServices,
      isDesktop,
      isRemovingService,
      getUserFavoritesServices,
      toggleUserFavoritesServices,
      hideServiceFromAccount,
      hideServiceFromEnvironment,
      revealServiceOnAccount,
      revealServiceOnEnvironment,
      isIdwAccount,
      idwInfo
    };
  }
});

const _hoisted_1 = {
  class: "layout-column",
  "data-test": "service-navigation"
};
const _hoisted_2 = {
  class: "layout-row h-2xl justify-between bg-black px-md md:px-xl"
};
const _hoisted_3 = {
  class: "layout-row grow gap-md overflow-hidden sm:gap-xl"
};
const _hoisted_4 = {
  class: "layout-row gap-md pl-sm sm:pl-xl"
};
const _hoisted_5 = {
  key: 2,
  "data-test": "account-container"
};
const _hoisted_6 = {
  key: 0,
  class: "typography-body-2-bold"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_ServiceNavigationLogo = resolveComponent("ServiceNavigationLogo");
  const _component_ServiceNavigationDropdown = resolveComponent("ServiceNavigationDropdown");
  const _component_ServiceNavigationContent = resolveComponent("ServiceNavigationContent");
  const _component_ServiceNavigationNotifications = resolveComponent("ServiceNavigationNotifications");
  const _component_ServiceNavigationUtilitiesMenu = resolveComponent("ServiceNavigationUtilitiesMenu");
  const _component_ServiceNavigationFooter = resolveComponent("ServiceNavigationFooter");
  const _component_OrConfirmV3 = resolveComponent("OrConfirmV3");
  return openBlock(), createElementBlock("header", _hoisted_1, [createElementVNode("div", _hoisted_2, [createElementVNode("div", _hoisted_3, [createVNode(_component_ServiceNavigationLogo, {
    "logo-url": _ctx.logoUrl,
    "target-url": _ctx.targetUrl,
    service: _ctx.service
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "logo")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["logo-url", "target-url", "service"]), _ctx.showServices ? (openBlock(), createBlock(_component_ServiceNavigationDropdown, {
    key: 0,
    "dropdown-items": _ctx.dropdownItems,
    "project-label": _ctx.label || _ctx.projectLabel,
    "favorites-services": _ctx.getUserFavoritesServices,
    "idw-info": _ctx.idwInfo,
    "is-idw-account": _ctx.isIdwAccount,
    "project-id": _ctx.service,
    "user-role": _ctx.userRole,
    onToggleFavorite: _ctx.toggleUserFavoritesServices,
    onDelete: _ctx.onDelete,
    onHideFromAccount: _ctx.hideServiceFromAccount,
    onHideFromEnvironment: _ctx.hideServiceFromEnvironment,
    onRevealOnAccount: _ctx.revealServiceOnAccount,
    onRevealOnEnvironment: _ctx.revealServiceOnEnvironment
  }, null, 8 /* PROPS */, ["dropdown-items", "project-label", "favorites-services", "idw-info", "is-idw-account", "project-id", "user-role", "onToggleFavorite", "onDelete", "onHideFromAccount", "onHideFromEnvironment", "onRevealOnAccount", "onRevealOnEnvironment"])) : createCommentVNode("v-if", true), _ctx.isDesktop ? (openBlock(), createBlock(_component_ServiceNavigationContent, {
    key: 1,
    "menu-items": _ctx.menuItems
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "content")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["menu-items"])) : createCommentVNode("v-if", true)]), createElementVNode("div", _hoisted_4, [_ctx.showNotifications && _ctx.token && _ctx.config.NOTIFICATIONS_API_URL ? (openBlock(), createBlock(_component_ServiceNavigationNotifications, {
    key: 0,
    config: _ctx.config,
    token: _ctx.token,
    "account-id": _ctx.accountId
  }, null, 8 /* PROPS */, ["config", "token", "account-id"])) : createCommentVNode("v-if", true), _ctx.showUtilsMenu ? (openBlock(), createBlock(_component_ServiceNavigationUtilitiesMenu, {
    key: 1,
    config: _ctx.utilitiesMenuItemsConfig
  }, null, 8 /* PROPS */, ["config"])) : createCommentVNode("v-if", true), _ctx.$slots.account ? (openBlock(), createElementBlock("div", _hoisted_5, [renderSlot(_ctx.$slots, "account")])) : createCommentVNode("v-if", true)])]), createVNode(_component_ServiceNavigationFooter, {
    "breadcrumb-items": _ctx.breadcrumbItems,
    "menu-items": _ctx.menuItems,
    "data-testid": "navigation-footer"
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "footer")]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["breadcrumb-items", "menu-items"]), createVNode(_component_OrConfirmV3, {
    ref: "deleteConfirm",
    "confirm-type": "d",
    loading: _ctx.isRemovingService,
    "title-text": _ctx.t('deleteConfirmation.title'),
    "auto-close-on-confirm": false,
    onConfirm: _ctx.onConfirmDelete
  }, {
    default: withCtx(() => [createElementVNode("div", null, [createTextVNode(toDisplayString(_ctx.t('deleteConfirmation.message_before')) + " ", 1 /* TEXT */), _ctx.serviceToDelete ? (openBlock(), createElementBlock("span", _hoisted_6, toDisplayString(_ctx.t(`services.${_ctx.serviceToDelete}.label`)), 1 /* TEXT */)) : createCommentVNode("v-if", true), createTextVNode(" " + toDisplayString(_ctx.t('deleteConfirmation.message_after')), 1 /* TEXT */)])]),

    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["loading", "title-text", "onConfirm"])]);
}

script.render = render;
script.__file = "src/components/ServiceNavigationHeader.vue";

export { script as ServiceNavigationHeader };
